import {
  faFileLines,
  faTrash,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useStyles } from './MarketplaceTemplateMetadataTab.styles'
import { Typography } from '@mui/material'
import {
  ButtonV2,
  CheckBoxFieldv2,
  NestedDropdown,
  NoteV2,
  TableComponentV2,
  TextInputV4,
} from '@labrav/react-components'
import { ChangeEvent, useCallback, useMemo } from 'react'
import { shallowEqual, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useSelector } from '../../../../../../store'
import {
  MarketplaceFieldMappingMetadata,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../../../modules/marketplaceMappingManager/types'
import { updateMarketplaceTemplateSectionField } from '../../../../../../modules/marketplaceMappingManager/action'
import { buildSalesforceCrmNestedMenu } from '../../../../utils/mappingManagerConstants'
import { parseCrmField } from '../MarketplaceTemplateDimensions/MarketplaceTemplateDimensionTable'

const customerDetailsMapping = {
  Key: 'key',
  Object: 'salesforceFields',
  Value: 'value',
  'Read Only': 'readOnly',
  '': 'action',
}

const EMPTY_SELECTED: string[] = []
const NOOP = () => {}
const TABLE_CELL_PROPS = [
  { style: { width: '30%' } },
  { style: { width: '20%' } },
  { style: { width: '30%' } },
  { style: { width: '10%' } },
  { style: { width: '10%' } },
]

const EMPTY_METADATA_MAPPING: MarketplaceFieldMappingMetadata = {
  label: 'Metadata',
  picklist: [],
  required: false,
  crmField: '',
  crmFieldLabel: '',
  category: '',
  helpText: '',
  isHidden: false,
  isMapped: false,
  overwrite: false,
  maxLength: null,
  minLength: null,
  description: '',
  isReadonly: false,
  placeholder: '',
  defaultValue: [],
  displayOrder: 2,
  crmFieldType: '',
  referralField: 'Labra__Metadata2__c',
  picklistMappings: [],
  referralFieldType: 'Picklist',
}

export const MarketplaceTemplateMetadataTab = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const isViewOnly = queryParams.get('viewOnly') === 'true'
  const marketplaceTableDetails = useSelector(
    state =>
      state.marketplaceMappingManager?.templateData?.templateData?.fieldMappings
        ?.metadata?.metadataMapping,
    shallowEqual
  )

  const salesforceFieldsData = useSelector(
    state => state.marketplaceMappingManager.salesforceFields.fields || []
  )

  const marketplaceTableDetailsList = useMemo(() => {
    if (!marketplaceTableDetails) return []
    return Object.entries(marketplaceTableDetails).map(([key, value]) => ({
      key,
      value,
    }))
  }, [marketplaceTableDetails])

  const lengthOfMarketplaceTableDetails = useMemo(() => {
    if (!marketplaceTableDetails) return 0
    return Object.keys(marketplaceTableDetails).length + 1
  }, [marketplaceTableDetails])

  const crmMenu = useMemo(
    () => buildSalesforceCrmNestedMenu(salesforceFieldsData),
    [salesforceFieldsData]
  )

  const onAddMetadataButtonClick = () => {
    dispatch(
      updateMarketplaceTemplateSectionField(
        'metadata' as keyof MarketplaceTemplatesDataFieldMappings,
        {
          metadataMapping: {
            ...marketplaceTableDetails,
            [`labra_metadata${lengthOfMarketplaceTableDetails}_c`]:
              EMPTY_METADATA_MAPPING,
          },
        } as Partial<MarketplaceFieldMappingMetadata>
      )
    )
  }

  const onDeleteMetadataRow = useCallback(
    (rowKey: string) => {
      if (!marketplaceTableDetails) return
      const nextMetadataMapping = { ...marketplaceTableDetails }
      delete nextMetadataMapping[rowKey]

      dispatch(
        updateMarketplaceTemplateSectionField(
          'metadata' as keyof MarketplaceTemplatesDataFieldMappings,
          {
            metadataMapping: nextMetadataMapping,
          } as Partial<MarketplaceFieldMappingMetadata>
        )
      )
    },
    [dispatch, marketplaceTableDetails]
  )

  const onDefaultValueChange = useCallback(
    (rowKey: string, nextValue: string) => {
      if (!marketplaceTableDetails) return
      const currentRow = marketplaceTableDetails[rowKey]
      if (!currentRow) return

      dispatch(
        updateMarketplaceTemplateSectionField(
          'metadata' as keyof MarketplaceTemplatesDataFieldMappings,
          {
            metadataMapping: {
              ...marketplaceTableDetails,
              [rowKey]: {
                ...currentRow,
                defaultValue: nextValue,
                isMapped: !!nextValue,
              },
            },
          } as Partial<MarketplaceFieldMappingMetadata>
        )
      )
    },
    [dispatch, marketplaceTableDetails]
  )

  const onKeyFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, rowKey: string) => {
      if (!marketplaceTableDetails) return
      const nextKey = event?.target?.value

      dispatch(
        updateMarketplaceTemplateSectionField(
          'metadata' as keyof MarketplaceTemplatesDataFieldMappings,
          {
            metadataMapping: {
              ...marketplaceTableDetails,
              [rowKey]: {
                ...marketplaceTableDetails[rowKey],
                key: nextKey || '',
              },
            },
          } as Partial<MarketplaceFieldMappingMetadata>
        )
      )
    },
    [dispatch, marketplaceTableDetails]
  )

  const onReadOnlyChange = useCallback(
    (rowKey: string, nextOverwriteValue: boolean) => {
      if (!marketplaceTableDetails) return
      const currentRow = marketplaceTableDetails[rowKey]
      if (!currentRow) return

      dispatch(
        updateMarketplaceTemplateSectionField(
          'metadata' as keyof MarketplaceTemplatesDataFieldMappings,
          {
            metadataMapping: {
              ...marketplaceTableDetails,
              [rowKey]: {
                ...currentRow,
                isReadonly: nextOverwriteValue,
              },
            },
          } as Partial<MarketplaceFieldMappingMetadata>
        )
      )
    },
    [dispatch, marketplaceTableDetails]
  )

  function computeFieldIsMapped(
    row: MarketplaceFieldMappingMetadata,
    updates: Partial<MarketplaceFieldMappingMetadata>
  ): boolean {
    const merged: MarketplaceFieldMappingMetadata = { ...row, ...updates }

    return !!(String(merged?.crmField ?? '') || merged?.defaultValue)
  }

  const renderRow = useCallback(
    (rowKey: string, metadataObj: MarketplaceFieldMappingMetadata) => {
      const handlers = {
        onSalesforceChange: (
          selectedValues: string[],
          selectedPaths: string[]
        ) => {
          const selectedValue = selectedValues[0] || ''
          const selectedPath = selectedPaths[0] || ''
          const pathParts = selectedPath.split('.')
          const crmObject = pathParts[0] || ''
          const crmFieldLabelOnly = selectedValue
            ? pathParts.slice(1).join('.') || selectedPath
            : ''

          dispatch(
            updateMarketplaceTemplateSectionField(
              'metadata' as keyof MarketplaceTemplatesDataFieldMappings,
              {
                metadataMapping: {
                  ...marketplaceTableDetails,
                  [rowKey]: {
                    ...metadataObj,
                    crmField:
                      crmObject && selectedValue
                        ? `${crmObject}.${selectedValue}`
                        : selectedValue,
                    crmFieldLabel: crmFieldLabelOnly,
                    isMapped: computeFieldIsMapped(metadataObj, {
                      crmField:
                        crmObject && selectedValue
                          ? `${crmObject}.${selectedValue}`
                          : selectedValue,
                      crmFieldLabel: crmFieldLabelOnly,
                    }),
                  },
                },
              } as Partial<MarketplaceFieldMappingMetadata>
            )
          )
        },
        menuOption: crmMenu,
      }

      const crmParsed = parseCrmField(metadataObj?.crmField)
      const defaultSelectedItems =
        crmParsed && metadataObj?.crmFieldLabel
          ? [
              {
                value: crmParsed.fieldApi,
                path: `${crmParsed.objectType}.${metadataObj?.crmFieldLabel}`,
              },
            ]
          : []

      const isSalesforceFieldsDisabled = !!(metadataObj?.defaultValue || '')
      const isDefaultValueDisabled = !!(metadataObj?.crmField || '')
      return {
        key: (
          <div className={classes.inputText}>
            <TextInputV4
              title=""
              placeHolder=""
              updatedValue={metadataObj?.key || ''}
              value={metadataObj?.key || ''}
              isError={false}
              isReadOnly={false}
              isRequired={false}
              errorText=""
              showSpinner={false}
              onChange={e => onKeyFieldChange(e, rowKey)}
              keyName={`${rowKey}-key`}
            />
          </div>
        ),
        salesforceFields: (
          <div className={classes.inputSelect}>
            <NestedDropdown
              menuData={handlers.menuOption}
              placeholder="Select an option"
              width="381px"
              height="44px"
              allowMultiple={false}
              defaultSelectedItems={defaultSelectedItems}
              onSelectionChange={handlers.onSalesforceChange}
              disabled={isViewOnly || isSalesforceFieldsDisabled}
            />
          </div>
        ),
        value: (
          <div className={classes.inputText}>
            <TextInputV4
              title=""
              placeHolder=""
              updatedValue={
                typeof metadataObj?.defaultValue === 'string'
                  ? metadataObj.defaultValue
                  : ''
              }
              value={
                typeof metadataObj?.defaultValue === 'string'
                  ? metadataObj.defaultValue
                  : ''
              }
              isError={false}
              isReadOnly={isDefaultValueDisabled}
              isRequired={false}
              errorText=""
              showSpinner={false}
              onChange={e =>
                onDefaultValueChange(
                  rowKey,
                  (e.target as HTMLInputElement)?.value || ''
                )
              }
              keyName={`${rowKey}-value`}
            />
          </div>
        ),
        readOnly: (
          <CheckBoxFieldv2
            keyName={`${rowKey}-readOnly`}
            title=""
            value={!!metadataObj?.isReadonly}
            onChangeBox={(e: ChangeEvent<HTMLInputElement>) =>
              onReadOnlyChange(rowKey, !!e?.target?.checked)
            }
          />
        ),
        action: (
          <div className={classes.trashIcon}>
            <FontAwesomeIcon
              icon={faTrash}
              style={{ width: '14px', height: '20px' }}
              data-testid="trash-metaData"
              onClick={() => onDeleteMetadataRow(rowKey)}
            />
          </div>
        ),
      }
    },
    [
      classes,
      isViewOnly,
      onDefaultValueChange,
      onDeleteMetadataRow,
      onKeyFieldChange,
      onReadOnlyChange,
      marketplaceTableDetails,
      crmMenu,
      dispatch,
    ]
  )

  const marketplaceTableData = useMemo(() => {
    return marketplaceTableDetailsList.map(item =>
      renderRow(item.key, item.value as MarketplaceFieldMappingMetadata)
    )
  }, [marketplaceTableDetailsList, renderRow])

  const itemHeight = marketplaceTableDetailsList.length * 64

  return (
    <>
      <div
        data-testid="marketplace-recipient-source-tab"
        className={classes.tabWrapper}
      >
        <div className={classes.headingContainer}>
          <div className={classes.iconWrapper}>
            <FontAwesomeIcon icon={faFileLines} className={classes.icon} />
          </div>
          <Typography className={classes.heading}>Create Metadata</Typography>
        </div>
        <NoteV2
          description={
            'All the Global Metadata fields will be mandatory for each Offer/Agreement. For picklist, the first value will be considered default value.'
          }
        />
        <TableComponentV2
          keyName="marketplace-template-form-table"
          objects={marketplaceTableData}
          mapping={customerDetailsMapping}
          fetchMoreData={NOOP}
          selectedItem={EMPTY_SELECTED}
          onChangeCheckbox={NOOP}
          showCheckbox={false}
          breakWorkHeader={false}
          tableHeight={`calc(100%)`}
          hasMoreData={false}
          customCellProps={TABLE_CELL_PROPS}
        />
        <ButtonV2
          className={classes.widthButton}
          styleType="link"
          onClick={onAddMetadataButtonClick}
          data-testid="add-a-metaData-button"
        >
          <FontAwesomeIcon
            icon={faPlus}
            width={'14px'}
            height={'16px'}
            style={{ paddingRight: '13px', paddingLeft: '5px' }}
          />
          New metadata
        </ButtonV2>
      </div>
    </>
  )
}
